import type { Bi } from 'schools/jaimini/types/bilingual';

export type JaiminiTopic = {
  id: string;
  title: Bi;
  summary: Bi;
  readMinutes: number;
  icon: string;
};

export type JaiminiChapter = {
  id: string;
  number: number;
  title: Bi;
  subtitle: Bi;
  icon: string;
  topics: JaiminiTopic[];
};

export const JAIMINI_HERO = {
  eyebrow: {
    en: 'Sign-based study · Jaimini',
    hi: 'राशि-आधारित अध्ययन · जैमिनी',
  },
  titleShort: { en: 'Jaimini', hi: 'जैमिनी' },
  title: { en: 'Jaimini Jyotisha', hi: 'जैमिनी ज्योतिष' },
  motto: {
    en: 'Signs, karakas, and sutras - a distinct path from classical graha drishti.',
    hi: 'राशि, कारक और सूत्र - शास्त्रीय ग्रह दृष्टि से अलग एक मार्ग।',
  },
  tagline: {
    en: 'Sign-based karakas, rashi drishti, arudha padas, argala, and chara dasha - Upadesha Sutra methodology with clinical precision.',
    hi: 'राशि-आधारित कारक, राशि दृष्टि, आरूढ़ पद, अर्गला और चर दशा - उपदेश सूत्र विधि से नैदानिक सटीकता।',
  },
  intro: {
    en: 'Learn chara karakas, rashi drishti, arudha padas, argala, and chara dasha through the Upadesha Sutra method - sign-centric rules that complement, not replace, Parashari foundations.',
    hi: 'उपदेश सूत्र विधि से चर कारक, राशि दृष्टि, आरूढ़ पद, अर्गला और चर दशा सीखें - राशि-केंद्रित नियम जो पाराशरी आधार को पूरक हैं, प्रतिस्थापित नहीं।',
  },
  trait: {
    en: 'Sign-based · not Parashari graha drishti',
    hi: 'राशि-आधारित · पाराशरी ग्रह दृष्टि नहीं',
  },
};

function topic(
  id: string,
  titleEn: string,
  titleHi: string,
  summaryEn: string,
  summaryHi: string,
  readMinutes: number,
  icon: string,
): JaiminiTopic {
  return {
    id,
    title: { en: titleEn, hi: titleHi },
    summary: { en: summaryEn, hi: summaryHi },
    readMinutes,
    icon,
  };
}

export const JAIMINI_CHAPTERS: JaiminiChapter[] = [
  {
    id: 'foundations',
    number: 1,
    title: { en: 'Jaimini Foundations', hi: 'जैमिनी आधार' },
    subtitle: {
      en: 'Sutra philosophy, software calibration, and calculation standards',
      hi: 'सूत्र दर्शन, सॉफ्टवेयर अंशांकन और गणना मानक',
    },
    icon: '◎',
    topics: [
      topic('origins-of-the-name', 'Origins of the Name', 'नाम की उत्पत्ति', 'Lineage & meaning - Maharishi Jaimini, Purva Mimamsa, Upadesha Sutras, and a sign-centric system apart from Parashari graha drishti.', 'परंपरा और अर्थ - महर्षि जैमिनी, पूर्व मीमांसा, उपदेश सूत्र, और पाराशरी ग्रह दृष्टि से अलग राशि-केंद्रित पद्धति।', 20, '🕉'),
      topic('jaimini-philosophy-sutras', 'Philosophy of Jaimini (Sutra Decoding)', 'जैमिनी दर्शन (सूत्र विवेचन)', 'Decode the Upadesha Sutras as sign-centric karmic grammar.', 'उपदेश सूत्रों को राशि-केंद्रित कर्म व्याकरण के रूप में पढ़ें।', 18, '📜'),
      topic('jaimini-vs-parashari', 'Jaimini vs Parashari Framework', 'जैमिनी बनाम पाराशरी ढाँचा', 'Karmic destiny, societal projection, and soul pathing vs graha drishti.', 'कर्म भाग्य, सामाजिक प्रक्षेपण और आत्म मार्ग बनाम ग्रह दृष्टि।', 17, '⚖'),
      topic('nakshatra-to-rashi-shift', 'Nakshatra to Rashi Centricity', 'नक्षत्र से राशि केंद्रिता', 'Why Jaimini times life by signs and karakas, not Moon nakshatra alone.', 'जैमिनी जीवन को राशि और कारक से क्यों मापता है, केवल चंद्र नक्षत्र से नहीं।', 16, '↻'),
      topic('software-ayanamsha-standards', 'Software & Ayanamsha Standards', 'सॉफ्टवेयर और अयनांश मानक', 'Sidereal setup, D-1/D-9 charts, and reproducible Jaimini workflow.', 'निरयण सेटअप, डी-1/डी-9 और पुनरुत्पादनीय जैमिनी कार्यप्रणाली।', 17, '💻'),
      topic('lahiri-raman-pushya-paksha', 'Lahiri vs Raman vs Pushya-Paksha', 'लाहिरी बनाम रमन बनाम पुष्य-पक्ष', 'How ayanamsha choice shifts karaka order and dasha boundaries.', 'अयनांश चुनाव कारक क्रम और दशा सीमा कैसे बदलता है।', 16, '°'),
      topic('true-mean-nodes', 'True Nodes vs Mean Nodes', 'सत्य बनाम माध्य छाया ग्रह', 'Mathematical criteria for Rahu/Ketu in karaka and dasha calculations.', 'कारक और दशा गणना में राहु/केतु हेतु गणितीय मानदंड।', 16, '☊'),
      topic('degree-tie-resolution', 'Degree Tie Resolution Rules', 'अंश बराबरी सुलझाना', 'Minutes, seconds, and birth-time sensitivity when two planets tie.', 'दो ग्रह बराबर हों तो मिनट, सेकंड और जन्म समय संवेदनशीलता।', 17, '⚔'),
      topic('karaka-lopa-fallback', 'Karaka Lopa & Fallback Rules', 'कारक लोप और पुनर्स्थापन', 'When a planet drops from ranking due to ties - fallback mechanisms.', 'बराबरी से ग्रह क्रम से गिरे - पुनर्स्थापन नियम।', 16, '∅'),
      topic('jaimini-calculation-workflow', 'End-to-End Calculation Workflow', 'समग्र गणना कार्यप्रणाली', 'Cast → rank karakas → padas → dasha in one disciplined pipeline.', 'बनाएँ → कारक क्रम → पद → दशा एक अनुशासित क्रम में।', 18, '🔧'),
      topic('d1-d9-discipline', 'D-1 & D-9 Chart Discipline', 'डी-1 और डी-9 अनुशासन', 'Rashi chart for signs; Navamsha for karakamsha and lagnamsha.', 'राशि चक्र संकेतों के लिए; नवांश कारकांश और लग्नांश के लिए।', 14, '⑨'),
    ],
  },
  {
    id: 'karakas',
    number: 2,
    title: { en: 'Chara Karakas', hi: 'चर कारक' },
    subtitle: {
      en: 'Seven and eight karaka schemes and individual life targets',
      hi: 'सात और आठ कारक योजना और व्यक्तिगत जीवन लक्ष्य',
    },
    icon: '♔',
    topics: [
      topic('seven-vs-eight-karaka-debate', 'The 7 vs 8 Karaka Debate', '7 बनाम 8 कारक विवाद', 'Classical schools: Sun–Saturn only vs Rahu with inverted degree.', 'शास्त्रीय विद्यालय: केवल सूर्य–शनि बनाम उलटे अंश वाला राहु।', 17, '⚖'),
      topic('seven-karaka-rules', '7-Karaka Rules (Excluding Rahu)', '7-कारक नियम (राहु रहित)', 'AK through DK from seven classical planets ranked by degree.', 'सात शास्त्रीय ग्रहों के अंश क्रम से आत्मकारक से दाराकारक।', 18, '⑦'),
      topic('eight-karaka-rahu-inversion', '8-Karaka & Rahu Inversion', '8-कारक और राहु उलटाव', 'Effective degree = 30° − Rahu longitude; separate Pitrikaraka.', 'प्रभावी अंश = 30° − राहु देशांतर; अलग पितृकारक।', 17, '☊'),
      topic('atmakaraka-ak', 'Atmakaraka (AK): Soul King', 'आत्मकारक: आत्मा राजा', 'Highest degree planet - soul desire, karmic lesson, ultimate destiny.', 'उच्चतम अंश ग्रह - आत्मा इच्छा, कर्म पाठ, परम भाग्य।', 19, '☉'),
      topic('amatyakaraka-amk', 'Amatyakaraka (AmK): Prime Minister', 'अमात्यकारक: प्रधान मंत्री', 'Career execution, advisors, financial strategy, manifesting AK agenda.', 'करियर क्रियान्वयन, सलाहकार, वित्तीय रणनीति, आत्मकारक एजेंडा।', 18, '♃'),
      topic('bhratrukaraka-bk', 'Bhratrukaraka (BK): Siblings & Mentors', 'भ्रातृकारक: भाई और गुरु', 'Siblings, courage, gurus, immediate circle of influence.', 'भाई-बहन, साहस, गुरु, निकट प्रभाव मंडल।', 16, '♂'),
      topic('matrukaraka-mk', 'Matrukaraka (MK): Mother & Security', 'मातृकारक: माता और सुरक्षा', 'Mother, landed property, domestic peace, foundational security.', 'माता, भूमि संपत्ति, गृह शांति, आधारभूत सुरक्षा।', 16, '☽'),
      topic('putrakaraka-pk', 'Putrakaraka (PK): Children & Creativity', 'पुत्रकारक: संतान और सृजन', 'Children, creative intelligence, high-stakes investments, disciples.', 'संतान, रचनात्मक बुद्धि, बड़े निवेश, शिष्य।', 16, '♀'),
      topic('jnatikaraka-jk', 'Jnatikaraka (JK): Foes & Blockages', 'ज्ञातिकारक: शत्रु और रुकावट', 'Competitors, disease, maternal relatives, internal obstacles.', 'प्रतिद्वंद्वी, रोग, मातृ संबंधी, आंतरिक बाधा।', 16, '♄'),
      topic('darakaraka-dk', 'Darakaraka (DK): Spouse & Partners', 'दाराकारक: पति/पत्नी और साझेदार', 'Spouse, serious romance, business associates, material grounding.', 'पति/पत्नी, गंभीर प्रेम, व्यापारिक साझेदार, भौतिक आधार।', 17, '💍'),
      topic('pitrikaraka-eight-scheme', 'Pitrikaraka in 8-Karaka Scheme', '8-कारक में पितृकारक', 'Father significator when Rahu expands the karaka table.', 'राहु से कारक तालिका विस्तार पर पिता कारक।', 14, '☉'),
      topic('chara-karaka-drill', 'Chara Karaka Calculation Drill', 'चर कारक गणना अभ्यास', 'Step-by-step ranking exercise with tie-handling examples.', 'बराबरी उदाहरण सहित चरणबद्ध क्रम अभ्यास।', 18, '🎯'),
    ],
  },
  {
    id: 'geometry',
    number: 3,
    title: { en: 'Jaimini Geometry', hi: 'जैमिनी ज्यामिति' },
    subtitle: {
      en: 'Rashi drishti, argala, and virodhargala mechanics',
      hi: 'राशि दृष्टि, अर्गला और विरोधार्गला यांत्रिकी',
    },
    icon: '🔗',
    topics: [
      topic('rashi-drishti-geometry', 'Rashi Drishti Geometry', 'राशि दृष्टि ज्यामिति', 'Signs aspect signs - independent of planetary graha drishti.', 'राशियाँ राशियों को देखती हैं - ग्रह दृष्टि से स्वतंत्र।', 19, '⇄'),
      topic('chara-sthira-dvisvabhava', 'Chara, Sthira & Dvisvabhava Signs', 'चर, स्थिर और द्विस्वभाव राशि', 'Movable, fixed, and dual modalities define aspect networks.', 'चर, स्थिर और द्वि स्वभाव दृष्टि जाल निर्धारित करते हैं।', 17, '△'),
      topic('movable-fixed-aspect-rules', 'Movable ↔ Fixed Aspect Rules', 'चर ↔ स्थिर दृष्टि नियम', 'Chara aspects Sthira (skip adjacent); Sthira aspects Chara back.', 'चर स्थिर को देखे (सन्निकट छोड़कर); स्थिर चर को वापस।', 18, '→'),
      topic('dual-sign-mutual-aspects', 'Dual Sign Mutual Aspects', 'द्वि राशि पारस्परिक दृष्टि', 'Dvisvabhava signs aspect each other mutually.', 'द्विस्वभाव राशियाँ आपस में एक-दूसरे को देखें।', 16, '↔'),
      topic('adjacent-sign-exception', 'The Adjacent Sign Exception', 'सन्निकट राशि अपवाद', 'Why movable never aspects its immediate neighbor.', 'चर राशि अपने सन्निकट पड़ोसी को क्यों नहीं देखती।', 16, '⊘'),
      topic('planetary-activation-vectors', 'Planetary Activation via Signs', 'राशि से ग्रह सक्रियण', 'Planets inherit power from sign vectors they occupy.', 'ग्रह अपनी राशि के सदिश से शक्ति ग्रहण करते हैं।', 16, '⚡'),
      topic('primary-argala-2-4-11', 'Primary Argala (2nd, 4th, 11th)', 'प्राथमिक अर्गला (2, 4, 11)', 'Decisive intervention on a sign or karaka from these houses.', 'इन भावों से राशि या कारक पर निर्णायक हस्तक्षेप।', 18, '⧉'),
      topic('secondary-argala-5th', 'Secondary Argala (5th House)', 'द्वितीयक अर्गला (5वाँ भाव)', 'Subtle emotional and creative intervention layer.', 'सूक्ष्म भावनात्मक और रचनात्मक हस्तक्षेप परत।', 16, '⑤'),
      topic('virodhargala-obstruction', 'Virodhargala Obstruction', 'विरोधार्गला रुकावट', '12th blocks 2nd, 10th blocks 4th, 3rd blocks 11th argala.', '12वाँ 2 को, 10वाँ 4 को, 3रा 11 को अर्गला रोके।', 17, '⊗'),
      topic('subha-asubha-argala', 'Subha vs Asubha Argala', 'शुभ बनाम अशुभ अर्गला', 'Benefic vs malefic intervention outcomes on life areas.', 'जीवन क्षेत्रों पर शुभ बनाम पापी हस्तक्षेप परिणाम।', 16, '☯'),
      topic('argala-synthesis-method', 'Argala Synthesis Method', 'अर्गला संश्लेषण विधि', 'Weigh argala vs virodhargala before declaring results.', 'फलित से पहले अर्गला बनाम विरोधार्गला तौलें।', 18, '🧩'),
    ],
  },
  {
    id: 'padas',
    number: 4,
    title: { en: 'Arudha Padas', hi: 'आरूढ़ पद' },
    subtitle: {
      en: 'Projection of reality - status, relationships, and soul anchors',
      hi: 'वास्तविकता का प्रक्षेपण - प्रतिष्ठा, संबंध और आत्मा आधार',
    },
    icon: '⌂',
    topics: [
      topic('arudha-lagna-formula', 'Arudha Lagna Formula (AL)', 'आरूढ़ लग्न सूत्र (AL)', 'Count from house to lord, then same distance from lord.', 'भाव से स्वामी तक गिनें, फिर स्वामी से उतनी दूरी।', 19, '👤'),
      topic('arudha-exception-swastha-sthita', 'Swastha & Sthita Exceptions', 'स्वस्थ और स्थित अपवाद', 'When lord falls in 1st or 7th from its house - pada rules shift.', 'स्वामी अपने भाव से 1 या 7 में हो - पद नियम बदलें।', 17, '⚠'),
      topic('reading-arudha-lagna-status', 'Reading AL: Status & Reputation', 'AL पढ़ना: प्रतिष्ठा और कीर्ति', 'Financial tier, public image, how the world perceives the native.', 'वित्तीय स्तर, सार्वजनिक छवि, संसार की दृष्टि।', 18, '★'),
      topic('bhava-padas-a2-a12', 'The 12 Bhava Padas (A2–A12)', '12 भाव पद (A2–A12)', 'Each house’s arudha reflection for outward manifestation.', 'प्रत्येक भाव का बाहरी प्रतिबिंब आरूढ़ पद।', 17, '▦'),
      topic('dhana-pada-a2', 'Dhana Pada (A2): Liquid Assets', 'धन पद (A2): तरल संपत्ति', 'Realized cash pools, savings visibility, wealth reflection.', 'वास्तविक नकदी, बचत दृश्यता, धन प्रतिबिंब।', 16, '💰'),
      topic('darapada-a7', 'Darapada (A7): Partnerships', 'दारापद (A7): साझेदारी', 'Business deals, physical interactions, commercial alliances.', 'व्यापारिक सौदे, भौतिक संपर्क, वाणिज्यिक गठबंधन।', 16, '🤝'),
      topic('upapada-lagna-ul', 'Upapada Lagna (UL)', 'उपपद लग्न (UL)', 'Arudha of 12th house - long-term committed bonds and marriage.', '12वें भाव का आरूढ़ - दीर्घकालिक बंधन और विवाह।', 18, '💍'),
      topic('second-from-ul-longevity', '2nd from UL: Relationship Longevity', 'UL से 2: संबंध दीर्घायु', 'Sustainability, separation reasons, and marital stability markers.', 'टिकाऊपन, वियोग कारण, वैवाहिक स्थिरता चिह्न।', 17, '②'),
      topic('karakamsha-lagna', 'Karakamsha Lagna', 'कारकांश लग्न', 'Atmakaraka’s Navamsha sign mapped back onto D-1.', 'आत्मकारक की नवांश राशि डी-1 पर वापस मैप।', 18, '◎'),
      topic('lagnamsha-navamsha', 'Lagnamsha & Navamsha Soul Reading', 'लग्नांश और नवांश आत्मा पठन', 'AK as 1st house in D-9 - moksha, hidden talents, divine purpose.', 'डी-9 में आत्मकारक पहला भाव - मोक्ष, छिपी प्रतिभा, दैवीय उद्देश्य।', 17, '☽'),
      topic('pada-yoga-financial-markers', 'Pada Yogas & Financial Markers', 'पद योग और वित्तीय चिह्न', 'Wealth combinations relative to AL and 7th from AL.', 'AL और AL से 7वें के सापेक्ष धन संयोग।', 17, '📈'),
      topic('arudha-synthesis-method', 'Arudha Synthesis Method', 'आरूढ़ संश्लेषण विधि', 'Layer AL, UL, A2, and karakamsha into one reading.', 'AL, UL, A2 और कारकांश एक फलित में मिलाएँ।', 18, '🎯'),
    ],
  },
  {
    id: 'dashas',
    number: 5,
    title: { en: 'Jaimini Dashas', hi: 'जैमिनी दशा' },
    subtitle: {
      en: 'Sign-based dashas - chara, sthira, manduka, and shoola',
      hi: 'राशि-आधारित दशा - चर, स्थिर, मंडूक और शूल',
    },
    icon: '⏳',
    topics: [
      topic('chara-dasha-overview', 'Chara Dasha Overview', 'चर दशा अवलोकन', 'Primary Jaimini timing - signs rule periods, not planets.', 'मुख्य जैमिनी समय - ग्रह नहीं, राशि अवधि शासन करती है।', 18, '→'),
      topic('dasha-direction-odd-even', 'Dasha Direction: Odd vs Even Signs', 'दशा दिशा: विषम बनाम सम राशि', 'Forward vs reverse zodiacal progression from lagna or karakamsha.', 'लग्न या कारकांश से आगे बनाम पीछे राशिचक्र प्रगति।', 19, '↻'),
      topic('chara-dasha-duration', 'Chara Dasha Duration Calculation', 'चर दशा अवधि गणना', 'Count from sign to lord; years = distance − 1 (up to 12 years).', 'राशि से स्वामी तक गिनें; वर्ष = दूरी − 1 (अधिकतम 12)।', 18, '⑫'),
      topic('antardasha-pratyantardasha', 'Antardasha & Pratyantardasha', 'अंतर्दशा और प्रत्यंतरदशा', 'Sub-period structure following same odd/even sign rules.', 'वही विषम/सम नियम से उप-अवधि संरचना।', 17, '⑪'),
      topic('chara-dasha-predictive', 'Predictive Application of Chara Dasha', 'चर दशा भविष्यवाणी अनुप्रयोग', 'Shift active dasha sign to 1st house - read events.', 'सक्रिय दशा राशि को 1 भाव बनाएँ - घटनाएँ पढ़ें।', 19, '📖'),
      topic('sthira-dasha-health', 'Sthira Dasha: Health & Longevity', 'स्थिर दशा: स्वास्थ्य और आयु', 'Fixed timing for health crises and physical transitions.', 'स्वास्थ्य संकट और शारीरिक संक्रमण का स्थिर समय।', 17, '⏸'),
      topic('manduka-dasha-frog-leap', 'Manduka Dasha (Frog Leap)', 'मंडूक दशा (मेंढक कूद)', 'Leapfrog jumps across signs for sudden unexpected events.', 'अचानक घटनाओं हेतु राशियों में कूद क्रम।', 17, '🐸'),
      topic('nirayana-shoola-dasha', 'Nirayana Shoola Dasha', 'निरयण शूल दशा', 'Diagnostic tool for critical health seasons and longevity.', 'गंभीर स्वास्थ्य काल और आयु का नैदानिक उपकरण।', 18, '☠'),
      topic('dasha-sign-as-lagna', 'Dasha Sign as Temporary Lagna', 'दशा राशि अस्थायी लग्न', 'Treat running sign as ascendant for event isolation.', 'चल रही राशि को घटना पृथक्करण हेतु लग्न मानें।', 17, '①'),
      topic('timing-synthesis-vimshottari', 'Blending Chara with Vimshottari', 'चर और विंशोत्तरी मिलान', 'Cross-verification when both dashas point to same window.', 'दोनों दशा एक खिड़की दिखाएँ तो पारस्परिक सत्यापन।', 18, '🧩'),
      topic('year-isolation-dasha-transits', 'Year Isolation: Dasha + Transits', 'वर्ष पृथक्करण: दशा + गोचर', 'Isolate exact year from dasha lord transits and argala.', 'दशा स्वामी गोचर और अर्गला से सटीक वर्ष निकालें।', 17, '📅'),
    ],
  },
  {
    id: 'practicum',
    number: 6,
    title: { en: 'Raja Yogas', hi: 'राजयोग' },
    subtitle: {
      en: 'Bala metrics, elite alignments, ethics, and blind readings',
      hi: 'बल माप, श्रेष्ठ संयोग, नैतिकता और अंध फलित',
    },
    icon: '★',
    topics: [
      topic('sthana-bala-jaimini', 'Sthana Bala in Jaimini', 'जैमिनी में स्थान बल', 'Sign strength and occupancy power for Jaimini judgment.', 'जैमिनी निर्णय हेतु राशि बल और उपस्थिति शक्ति।', 17, '⚡'),
      topic('drik-bala-karaka-bala', 'Drik Bala & Karaka Bala', 'दृक बल और कारक बल', 'Aspectual strength and karaka office power in synthesis.', 'दृष्टि बल और संश्लेषण में कारक पद शक्ति।', 17, '👁'),
      topic('co-lord-scorpio-aquarius', 'Co-Lord Conflict: Scorpio & Aquarius', 'सह-स्वामी विवाद: वृश्चिक और कुम्भ', 'Mars/Ketu and Saturn/Rahu tie-breakers for dasha years.', 'दशा वर्ष हेतु मंगल/केतु और शनि/राहु निर्णय।', 18, '♒'),
      topic('raja-yoga-ak-amk', 'Raja Yoga: AK & AmK Conjunction', 'राजयोग: आत्मकारक और अमात्यकारक', 'Soul-king meets prime minister - elite power alignment.', 'आत्मा राजा मिले प्रधान मंत्री - श्रेष्ठ शक्ति संरेखण।', 18, '👑'),
      topic('raja-yoga-trine-lords', 'Raja Yoga: AK, AmK & Trine Lords', 'राजयोग: आत्मकारक, अमात्य और त्रिकोण स्वामी', '5th and 9th lord links through signs and argala.', '5वें और 9वें स्वामी राशि और अर्गला से जुड़ाव।', 18, '✦'),
      topic('pada-yoga-arudha-wealth', 'Pada Yogas & Arudha Wealth', 'पद योग और आरूढ़ धन', 'High financial combinations from AL and 7th from AL.', 'AL और AL से 7वें से उच्च वित्तीय संयोग।', 17, '💎'),
      topic('clinical-integration-matrix', 'Clinical Integration Matrix', 'नैदानिक एकीकरण मैट्रिक्स', 'Blend Parashari Vimshottari with Jaimini chara dasha.', 'पाराशरी विंशोत्तरी जैमिनी चर दशा से मिलाएँ।', 19, '🔬'),
      topic('ethics-atmakaraka-karma', 'Ethics: Atmakaraka & Deep Karma', 'नैतिकता: आत्मकारक और गहन कर्म', 'Compassionate delivery of soul-level and spiritual bottlenecks.', 'आत्म-स्तर और आध्यात्मिक अवरोध करुणापूर्ण प्रस्तुति।', 16, '💔'),
      topic('blind-reading-jaimini', 'Blind Reading Protocol', 'अंध फलित प्रोटोकॉल', 'Declare karaka facts, AL status, and UL marriage markers first.', 'पहले कारक तथ्य, AL स्थिति और UL विवाह चिह्न घोषित करें।', 18, '⚡'),
      topic('case-study-workflow-jaimini', 'Case Study Workflow', 'केस अध्ययन कार्यप्रणाली', 'Verify AK, dasha, and pada predictions against biography.', 'जीवन वृत्त से आत्मकारक, दशा, पद फलित सत्यापित करें।', 18, '🔍'),
      topic('professional-closure-jaimini', 'Professional Closure & Certification', 'व्यावसायिक समापन और प्रमाणन', 'Close consultations, follow-up plan, continued Jaimini study path.', 'परामर्श समापन, अनुवर्ती योजना, जैमिनी अध्ययन मार्ग।', 14, '🎓'),
    ],
  },
];

const topicById = new Map<string, { topic: JaiminiTopic; chapter: JaiminiChapter }>();

for (const chapter of JAIMINI_CHAPTERS) {
  for (const t of chapter.topics) {
    topicById.set(t.id, { topic: t, chapter });
  }
}

export function getJaiminiTopic(id: string) {
  return topicById.get(id);
}

export function getJaiminiChapter(chapterId: string): JaiminiChapter | undefined {
  return JAIMINI_CHAPTERS.find((c) => c.id === chapterId);
}

export function getJaiminiChapterIndex(chapterId: string): number {
  return JAIMINI_CHAPTERS.findIndex((c) => c.id === chapterId);
}

export function getJaiminiChapterNeighbors(chapterId: string): {
  prev: JaiminiChapter | null;
  next: JaiminiChapter | null;
} {
  const idx = getJaiminiChapterIndex(chapterId);
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: idx > 0 ? JAIMINI_CHAPTERS[idx - 1] : null,
    next: idx < JAIMINI_CHAPTERS.length - 1 ? JAIMINI_CHAPTERS[idx + 1] : null,
  };
}

export function getJaiminiChapterReadMinutes(chapter: JaiminiChapter): number {
  return chapter.topics.reduce((sum, t) => sum + t.readMinutes, 0);
}

export function getAllJaiminiTopics(): JaiminiTopic[] {
  return JAIMINI_CHAPTERS.flatMap((c) => c.topics);
}

export function getJaiminiTopicCount(): number {
  return getAllJaiminiTopics().length;
}

const flatTopicOrder = JAIMINI_CHAPTERS.flatMap((c) => c.topics);

export function getJaiminiTopicNeighbors(topicId: string): {
  prev: JaiminiTopic | null;
  next: JaiminiTopic | null;
} {
  const idx = flatTopicOrder.findIndex((t) => t.id === topicId);
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: idx > 0 ? flatTopicOrder[idx - 1] : null,
    next: idx < flatTopicOrder.length - 1 ? flatTopicOrder[idx + 1] : null,
  };
}
